
import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface CanvasGridProps {
  className?: string;
  opacity?: number;
  size?: number;
  dotSize?: number;
  color?: string;
  animated?: boolean;
  gradientColors?: string[];
}

export const CanvasGrid = ({ 
  className, 
  opacity = 0.2,
  size = 15,
  dotSize = 1.5,
  color = "#0EA5E9",
  animated = false,
  gradientColors = ["#3b82f6", "#8b5cf6", "#6366f1"]
}: CanvasGridProps) => {
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  
  // Track mouse movement for the dynamic grid effect
  useEffect(() => {
    if (!animated) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      setHoverPosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [animated]);

  // Generate a radial gradient based on mouse position
  const getRadialGradient = () => {
    if (!animated) return '';
    
    return `radial-gradient(
      circle 150px at ${hoverPosition.x}px ${hoverPosition.y}px,
      ${gradientColors[0]}30 0%,
      ${gradientColors[1]}20 35%,
      transparent 70%
    )`;
  };

  return (
    <div className={cn("absolute inset-0 pointer-events-none", className)}>
      {/* Base grid layer */}
      <div 
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          backgroundImage: `
            radial-gradient(circle at 1px 1px, ${color} ${dotSize}px, transparent ${dotSize}px)
          `,
          backgroundSize: `${size}px ${size}px`,
          opacity: opacity
        }}
        aria-hidden="true"
      />
      
      {/* Animated highlight layer */}
      {animated && (
        <div 
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            backgroundImage: getRadialGradient(),
            opacity: 0.6,
            mixBlendMode: "overlay"
          }}
        />
      )}
      
      {/* Subtle gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom right, ${gradientColors[0]}05, ${gradientColors[1]}05, ${gradientColors[2]}05)`,
          opacity: 0.7
        }}
      />
    </div>
  );
};
